import { useState } from 'react';
import { DialogTrigger } from 'react-aria-components';

import { ImportJobStatus } from '@ekhodealer/ekho-common';
import {
	Button,
	Callout,
	Link,
	Popup,
	Stack,
	Table,
	Text,
	toast,
} from '@ekhodealer/ekho-common/rev';

import { Dropzone } from '@common/components/Dropzone';
import { PLACEHOLDER } from '@common/lib/strings';

import { useImportInventory } from '../api/useImportInventory';

interface ImportInventoryModalProps {
	isOpen: boolean;
	onOpenChange: (open: boolean) => void;
}

export const ImportInventoryModal = ({ isOpen, onOpenChange }: ImportInventoryModalProps) => {
	const [selectedFile, setSelectedFile] = useState<File | null>(null);
	const importMutation = useImportInventory();

	const handleFilesDropped = async (
		files: File[],
		errors?: {
			error: Error;
			fileName: string;
			mimeType: string;
		}[],
	) => {
		// Reset previous import results when selecting a new file
		importMutation.reset();

		// Handle errors from file validation
		if (errors && errors.length > 0) {
			errors.forEach((err) => {
				toast({
					message: `${err.fileName}: ${err.error.message}`,
					variant: 'error',
				});
			});
		}

		// Validate CSV files
		const validFiles: File[] = [];
		const invalidFiles: string[] = [];

		files.forEach((file) => {
			if (file.name.endsWith('.csv')) {
				validFiles.push(file);
			} else {
				invalidFiles.push(file.name);
			}
		});

		if (invalidFiles.length > 0) {
			toast({
				message: `Please select CSV files. Invalid files: ${invalidFiles.join(', ')}`,
				variant: 'error',
			});
		}

		// Set the first valid file (CSV import typically expects single file)
		if (validFiles.length > 0) {
			setSelectedFile(validFiles[0]);
		}
	};

	const handleImport = async () => {
		if (!selectedFile) {
			toast({ message: 'Please select a file to import', variant: 'error' });
			return;
		}

		try {
			const job = await importMutation.mutateAsync(selectedFile);

			// Handle result based on job status - access status from job directly
			const jobStatus = job?.status;

			if (jobStatus === ImportJobStatus.COMPLETED) {
				const created = job.results?.created || 0;
				const failed = job.results?.failed || 0;

				if (failed === 0) {
					toast({
						message: `${created} inventory items imported`,
						variant: 'success',
					});
				}
			}

			// Reset file selection
			setSelectedFile(null);

			// Close modal on success
			if (jobStatus === ImportJobStatus.COMPLETED && (job.results?.failed || 0) === 0) {
				onOpenChange(false);
			}
		} catch {
			// Error will be shown in modal if job data is available
		}
	};

	const handleClose = () => {
		setSelectedFile(null);
		// Reset import results when closing modal
		importMutation.reset();
		onOpenChange(false);
	};

	const handleNewImport = () => {
		setSelectedFile(null);
		importMutation.reset();
	};

	const job = importMutation.data;
	const hasErrors = job?.results?.errors && job.results.errors.length > 0;

	const handleDialogOpenChange = (open: boolean) => {
		if (!open) {
			// Reset import results when modal is closed
			importMutation.reset();
			setSelectedFile(null);
		}
		onOpenChange(open);
	};

	return (
		<DialogTrigger isOpen={isOpen} onOpenChange={handleDialogOpenChange}>
			<Popup title='Import inventory' size={hasErrors ? 'large' : 'medium'}>
				<Stack padding='0' itemsSpacing='24'>
					{/* File upload - hide when there are errors */}
					{!hasErrors && (
						<Stack itemsSpacing='8'>
							<Dropzone
								isMultiple={false}
								supportedMimeTypes={['text/csv', 'application/vnd.ms-excel']}
								isDisabled={importMutation.isPending}
								isLoading={importMutation.isPending}
								placeholder={{
									title: 'Drop CSV file here',
									description: (
										<Text size='bodySmall' color='secondary'>
											or click to browse your computer
										</Text>
									),
								}}
								onFilesDropped={handleFilesDropped}
							/>
							<Text size='bodySmall' color='secondary'>
								Upload a CSV file with inventory data. Required columns: make,
								model, modelYear, newUsed, odometerReading, map, and either vin or
								vehicleDetailsUrl.
							</Text>
						</Stack>
					)}

					{/* Error callout */}
					{hasErrors && (
						<Callout
							variant='error'
							appearance='filled'
							title={`Import blocked by ${job!.results!.errors.length} error${
								job!.results!.errors.length !== 1 ? 's' : ''
							}`}
							message={`Resolve the issue${
								job!.results!.errors.length !== 1 ? 's' : ''
							} below and re-upload your file.`}
						/>
					)}

					{/* Errors table - show when validation fails or when there are processing errors */}
					{hasErrors && (
						<Stack itemsSpacing='8'>
							<div
								style={{
									maxHeight: '300px',
									overflowY: 'auto',
								}}>
								<Table
									columns={[
										{
											id: 'row',
											title: 'Row',
											renderCell: (error) => error.rowNumber,
										},
										{
											id: 'column',
											title: 'Column(s)',
											renderCell: (error) => error.column || PLACEHOLDER,
										},
										{
											id: 'error',
											title: 'Error',
											renderCell: (error) => error.message,
										},
									]}
									items={job!.results!.errors}
									getItemId={(error) =>
										`error-${error.rowNumber}-${error.column || 'none'}-${
											error.message
										}`
									}
									aria-label='Import errors'
								/>
							</div>
						</Stack>
					)}

					{/* Action buttons */}
					<div
						style={{
							display: 'flex',
							justifyContent: 'space-between',
							alignItems: 'center',
							gap: '12px',
							paddingTop: '8px',
						}}>
						<Link
							href='https://docs.google.com/spreadsheets/d/1ZUQwyPYpBYBEd8pbXss7GLwBFU27_jjNVCksSkmtKIg/edit?usp=sharing'
							target='_blank'
							rel='noopener noreferrer'
							style={{
								fontWeight: 'var(--rev-fontWeight-regular)',
								color: 'var(--rev-color-brand)',
							}}>
							<Text size='bodySmall' weight='regular'>
								Download sample csv
							</Text>
						</Link>
						<div style={{ display: 'flex', gap: '12px' }}>
							{hasErrors ? (
								<>
									<Button
										variant='secondary'
										size='large'
										onPress={handleClose}
										isDisabled={importMutation.isPending}>
										Do it later
									</Button>
									<Button
										variant='filled'
										size='large'
										onPress={handleNewImport}
										isDisabled={importMutation.isPending}>
										Import again
									</Button>
								</>
							) : (
								<>
									<Button
										variant='secondary'
										size='large'
										onPress={handleClose}
										isDisabled={importMutation.isPending}>
										Cancel
									</Button>
									<Button
										variant='filled'
										size='large'
										onPress={handleImport}
										isLoading={importMutation.isPending}
										isDisabled={!selectedFile || importMutation.isPending}>
										Import
									</Button>
								</>
							)}
						</div>
					</div>
				</Stack>
			</Popup>
		</DialogTrigger>
	);
};
